import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CalendarDays, ImageIcon, Loader2, Pencil, Plus, Repeat, RotateCcw, Trash2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { BannerFormDialog, AUDIENCE_LABELS } from '../components/BannerFormDialog';
import { bannerImageSrc } from '../components/DashboardWelcomeBanner';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/dashboard-banners`;

const STATUS = { default: ['Default', 'outline'], showing: ['Showing', 'default'], off: ['Off', 'secondary'], scheduled: ['Scheduled', 'outline'], ended: ['Ended', 'secondary'] };
const fmtDay = (d) => new Date(`${d}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const BannerRow = ({ b, onEdit, onToggle, onDelete, onReset }) => (
  <div className="flex flex-col md:flex-row md:items-center gap-4 p-4" data-testid={`banner-row-${b.id}`}>
    <img src={bannerImageSrc(b.image_url)} alt={b.name} className="w-full md:w-56 aspect-[2000/453] object-cover object-right rounded-lg border" />
    <div className="flex-1 min-w-0">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold">{b.name}</span>
        <Badge variant={STATUS[b.status][1]} data-testid={`banner-status-${b.id}`}>{STATUS[b.status][0]}</Badge>
        <Badge variant="outline">{AUDIENCE_LABELS[b.audience]}</Badge>
        {b.repeat_yearly && <Badge variant="outline" className="gap-1 border-[#922A8C]/40 text-[#922A8C]" data-testid={`banner-repeats-${b.id}`}><Repeat className="h-3 w-3" />Repeats yearly</Badge>}
      </div>
      {b.is_default && <p className="text-xs text-muted-foreground mt-1" data-testid={`banner-default-note-${b.id}`}>Shows whenever no seasonal banner is on</p>}
      {b.start_date && <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1" data-testid={`banner-dates-${b.id}`}><CalendarDays className="h-3.5 w-3.5" />{fmtDay(b.start_date)} – {fmtDay(b.end_date)}</p>}
      <p className="text-sm mt-1 truncate"><b>{b.greeting}, [first name]!</b> <span style={{ color: b.accent_color }}>{b.tagline}</span></p>
      {b.message && <p className="text-xs text-muted-foreground line-clamp-1">{b.message}</p>}
    </div>
    <div className="flex items-center gap-3 shrink-0">
      {b.is_default
        ? <Button size="sm" variant="ghost" onClick={() => onReset(b)} data-testid={`banner-reset-${b.id}`}><RotateCcw className="h-4 w-4 mr-1" />Reset</Button>
        : b.start_date
        ? <span className="text-xs text-muted-foreground w-[68px]">Automatic</span>
        : <label className="flex items-center gap-2 text-sm"><Switch checked={b.active} onCheckedChange={() => onToggle(b)} data-testid={`banner-toggle-${b.id}`} />Show</label>}
      <Button size="sm" variant="outline" onClick={() => onEdit(b)} data-testid={`banner-edit-${b.id}`}><Pencil className="h-4 w-4 mr-1" />Edit</Button>
      {!b.is_default && <Button size="sm" variant="ghost" className="text-destructive" onClick={() => onDelete(b)} aria-label="Delete banner" data-testid={`banner-delete-${b.id}`}><Trash2 className="h-4 w-4" /></Button>}
    </div>
  </div>
);

export default function DashboardBannersPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(() => {
    axios.get(API).then((r) => setData(r.data.banners)).catch((e) => toast.error(e.response?.data?.detail || 'Failed to load banners'));
  }, []);
  useEffect(() => { if (user?.role === 'system_admin') load(); }, [load, user?.role]);

  const toggle = async (b) => {
    try { await axios.post(`${API}/${b.id}/${b.active ? 'deactivate' : 'activate'}`); toast.success(b.active ? 'Banner switched off' : 'Banner is now showing on the dashboard'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not update the banner'); }
  };
  const reset = async (b) => {
    try { await axios.post(`${API}/${b.id}/reset`); toast.success('Default banner reset to the original'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not reset the banner'); }
  };
  const remove = async () => {
    try { await axios.delete(`${API}/${toDelete.id}`); toast.success('Banner deleted'); load(); } catch (e) { toast.error(e.response?.data?.detail || 'Could not delete'); }
    setToDelete(null);
  };

  if (user?.role !== 'system_admin') return <div className="p-6 text-muted-foreground" data-testid="banners-forbidden">Only System Administrators can manage dashboard banners.</div>;

  return (
    <div className="space-y-6" data-testid="dashboard-banners-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><ImageIcon className="h-6 w-6" />Dashboard Banners</h1>
          <p className="text-muted-foreground text-sm">Show a holiday or campaign banner with its own greeting on the MyHELP dashboard. When no banner is showing, staff see the regular banner.</p>
        </div>
        <Button onClick={() => { setEditing(null); setFormOpen(true); }} data-testid="add-banner-btn"><Plus className="h-4 w-4 mr-2" />New Banner</Button>
      </div>
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Banners</CardTitle>
          <CardDescription>The two default banners show whenever no seasonal banner is on. Banners with dates show from midnight on the start date through the end date (Central time) and switch themselves on and off. Banners without dates use the Show switch. A banner for a specific group shows ahead of an "Everyone" banner; otherwise the most recently started one shows.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {!data ? <Loader2 className="h-5 w-5 animate-spin m-6" /> : data.length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground" data-testid="banners-empty">No seasonal banners yet. Click New Banner to create one.</p>
          ) : (
            <div className="divide-y border-t">{data.map((b) => <BannerRow key={b.id} b={b} onEdit={(x) => { setEditing(x); setFormOpen(true); }} onToggle={toggle} onDelete={setToDelete} onReset={reset} />)}</div>
          )}
        </CardContent>
      </Card>
      <BannerFormDialog open={formOpen} onOpenChange={setFormOpen} banner={editing} onSaved={load} />
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this banner?</AlertDialogTitle>
            <AlertDialogDescription>"{toDelete?.name}" will be removed. This can't be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="confirm-delete-banner-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
